<template>
  <div v-if="isTwilioConfigured">
    <v-banner
      icon="mdi-account-multiple"
    >
      <h4>Parties to be notified</h4>

      <template
        #actions
      >
        <div class="pr-4">
          <v-btn
            color="secondary"
            depressed
            small
            @click="_ => togglePartiesSelection()"
          >
            {{ togglePartiesSelectionText }}
          </v-btn>
        </div>
      </template>
    </v-banner>

    <v-card
      v-for="party of availableParties"
      :key="`party_${party.id}`"
      class="my-4 notification-list"
      outlined
    >
      <v-list
        class="py-0"
        two-line
      >
        <v-list-item
          class="px-6"
          :class="{
            '-disabled-party': party.error,
            '-selectable-party': !party.error && !isPartySelected(party.id),
            'v-list-item--active selected-party': isPartySelected(party.id)
          }"
          :dark="isPartySelected(party.id)"
          :disabled="!!party.error"
          @click.stop="_ => togglePartySelection(party.id)"
        >
          <v-list-item-action>
            <v-checkbox
              color="primary"
              :input-value="isPartySelected(party.id) ? 'active' : null"
            />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>
              {{ party.name }}
              <v-btn
                class="ml-1 force-enable"
                icon
                x-small
                @click.stop="_ => openPartyEdit(party.id)"
              >
                <v-icon>
                  mdi-account-edit-outline
                </v-icon>
              </v-btn>
            </v-list-item-title>

            <v-list-item-subtitle v-if="party.error">
              <div class="text-body-2 red--text d-flex align-center">
                <v-icon
                  color="red"
                  left
                  small
                >
                  mdi-cellphone-message-off
                </v-icon>
                {{ party.error }}
              </div>
            </v-list-item-subtitle>

            <v-list-item-subtitle
              v-else
              class="d-flex"
            >
              <div class="text-body-2 d-flex align-center">
                <span class="text-body-2">
                  Text Number:
                </span>
                <span class="ml-1">
                  {{ party.phone.phone_number }}
                </span>
              </div>
              <div class="d-flex align-center pl-4">
                <span class="text-body-2">
                  Preferred Language:
                </span>
                <span class="ml-1">
                  {{ party.language }}
                </span>
              </div>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <div v-if="isPartySelected(party.id)">
          <v-subheader class="d-flex align-center inset">
            <span class="font-weight-bold">
              Notifications
            </span>
            <v-spacer />
            <v-btn
              class="mr-2"
              color="secondary"
              depressed
              small
              @click="_ => togglePartyNotificationsSelection(party.id)"
            >
              {{ togglePartyNotificationsSelectionText(party.id) }}
            </v-btn>
          </v-subheader>
          <v-divider inset />
          <v-list
            class="py-0 inset"
            two-line
          >
            <v-list-item-group
              v-model="selectedNotifications"
              active-class="primary--text"
              multiple
            >
              <div
                v-for="(notificationType, nidx) of availableNotificationTypes"
                :key="`party_${party.id}_notification_${notificationType.id}`"
              >
                <v-divider
                  v-if="nidx > 0"
                  inset
                />
                <v-list-item
                  class="px-6"
                  :disabled="notificationType.disabled"
                  :value="{ notificationTypeId: notificationType.id, partyId: party.id }"
                >
                  <template #default="{ active }">
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="active"
                      />
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>
                        <div class="d-flex align-center">
                          <span>
                            {{ notificationType.name }}
                          </span>
                          <v-chip
                            v-if="isPartyNotificationEdited(party.id, notificationType.id)"
                            class="ml-4"
                            color="success"
                            x-small
                          >
                            Modified
                          </v-chip>
                        </div>
                      </v-list-item-title>

                      <v-list-item-subtitle
                        class="d-flex"
                      >
                        <div
                          class="text-body-2 d-flex align-center"
                          :class="{ 'red--text': notificationType.isOverdue }"
                        >
                          Send date:
                          <span class="ml-1 text--primary">
                            {{ notificationType.date }}
                          </span>
                        </div>
                        <div class="text-body-2 d-flex align-center pl-4">
                          Notification Language:
                          <span class="ml-1 text--primary">
                            {{ notificationType.languages }}
                          </span>
                        </div>
                      </v-list-item-subtitle>

                      <span
                        v-if="notificationType.isOverdue"
                        class="red--text font-weight-bold text-body-2"
                      >
                        Cannot send a notification scheduled in the past. Update the action date or workflow to select this message.
                      </span>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn
                        depressed
                        :disabled="isPartyNotificationDisabled(party.id, notificationType.id)"
                        fab
                        x-small
                        @click.stop="_ => onOpenNotificationEditor(party, notificationType)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </div>
            </v-list-item-group>
          </v-list>
        </div>
      </v-list>
    </v-card>

    <NotificationEditor
      v-if="notificationEditorDialog.isVisible"
      v-model="notificationEditorDialog.isVisible"
      :entities="templateEntities"
      :template="notificationEditorDialog.template"
      :text="notificationEditorDialog.text"
      @save="text => saveNotificationEditorChanges(text)"
    />
  </div>

  <div v-else>
    <div class="pa-8">
      <v-alert
        border="left"
        icon="mdi-alert-outline"
        prominent
        type="warning"
      >
        In order to be able to send notifications to Parties, Twilio must be properly configured.
      </v-alert>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { clone } from 'lodash'
import moment from 'moment-timezone'

import { partyName } from '@/utilities'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { PARTY_NAMESPACE } from '@/store/party'
import { RECORD_NAMESPACE } from '@/store/record'
import { SHARED_NAMESPACE } from '@/store/shared'
import { SHORT_MESSAGE_NAMESPACE } from '@/store/short-message'
import { allowedNotificationEvents } from '@/store/docket-entry/partials/docket-entry-edit'

import NotificationEditor from './NotificationEditor.vue'

export default {
  name: 'DocketEntryAttendeeNotifications',

  components: {
    NotificationEditor,
  },

  model: {
    event: 'update',
    prop: 'attendeeNotifications',
  },

  props: {
    // eslint-disable-next-line vue/no-unused-properties
    attendeeNotifications: {
      type: Array,
      required: true,
    },

    docketEntry: {
      type: Object,
      required: true,
    },

    legalCase: {
      type: Object,
      default: null,
    },

    notifications: {
      type: Array,
      default: _ => ([]),
    },

    parties: {
      type: Array,
      required: true,
    },

    reschedule: {
      type: Boolean,
      default: false,
    },

    // eslint-disable-next-line vue/no-unused-properties
    valid: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      editedNotifications: [],
      isLoading: true,
      notificationEditorDialog: {
        isVisible: false,
        notificationTypeId: null,
        party: null,
        template: null,
        text: '',
      },

      record: null,
      selectedNotifications: [],
      selectedParties: [],
    }
  },

  computed: {
    activeNotificationTypeIds () {
      return this.activeNotificationTypes.map(ant => ant.id)
    },

    activeNotificationTypes () {
      return this.availableNotificationTypes.reduce((res, detn) => {
        if (!detn.disabled) res.push(detn)
        return res
      }, [])
    },

    activeParties () {
      return this.availableParties.reduce((res, cp) => {
        if (!cp.error) res.push(cp.id)
        return res
      }, [])
    },

    appointmentStartDateTime () {
      return `${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`
    },

    areAllPartiesSelected () {
      return this.selectedParties.length === this.activeParties.length
    },

    availableNotificationTypes () {
      return this.docketEntryType?.docket_entry_type_notifications?.reduce((res, detn) => {
        if (!detn) return res

        const action = this.reschedule ? 'reschedule' : (this.docketEntry.id ? 'edit' : 'create')

        const isAllowed = allowedNotificationEvents(action).includes(detn.event_type)
        if (!isAllowed && !this.originallySelectedNotificationTypeIds.includes(detn.id)) return res

        const template = this.shortMessageTemplates.
          find(smt => smt.id === detn.short_message_template_id)

        const translations = template.short_message_template_translations.
          reduce((res, smtt) => {
            const languageName = this.optionsLanguages?.find(ol => ol.id === smtt.language_id)?.name
            res.push({ ...smtt, language: languageName })
            return res
          }, [])

        const languages = translations.
          reduce((res, smtt) => {
            if (this.partyLanguages.includes(smtt.language)) res.push(smtt.language)
            return res
          }, []).
          sort((a, b) => {
            const x = (a.name || '').toUpperCase()
            const y = (b.name || '').toUpperCase()
            return ((x < y) ? -1 : ((x > y) ? 1 : 0))
          })

        const triggerDate = this.calculateNotificationTriggerDate(detn)
        const isOverdue = !!triggerDate && triggerDate.isBefore()

        res.push({
          date: !!triggerDate && triggerDate.isValid() ? triggerDate.format('lll zz') : 'Immediately',
          disabled: isOverdue || !isAllowed,
          id: detn.id,
          isOverdue,
          languages: languages.join(', '),
          name: template.title,
          translations,
          triggerAt: triggerDate ? triggerDate.format() : undefined,
        })

        return res
      }, []) || []
    },

    availableParties () {
      let parties = []

      if (this.record?.record_holder_party) {
        parties.push({ ...this.record?.record_holder_party, id: this.record?.record_holder_party_id })
      }

      if (this.record?.contact_party && this.record?.contact_party.id !== this.record?.record_holder_party.id) {
        parties.push({ ...this.record?.contact_party, id: this.record?.contact_party_id })
      }

      parties = parties.concat(this.parties || []).reduce((res, party) => {
        if (res.find(p => p.id === party.id)) return res

        const entry = {
          error: false,
          id: party.id,
          language: party.primary_language || party.learned_languages?.find(ll => ll.primary === true)?.language_string || 'English',
          name: partyName(party),
          phone: party.phones?.find(phone => phone.preferred_text === true),
        }

        if (!entry.phone?.phone_number) {
          entry.error = 'This Party has no preferred text phone number set!'
        } else if (entry.phone.phone_type === 'wa' && !this.twilioHasWhatsAppNumber) {
          entry.error = 'This party has a WhatsApp number set as preferred but WhatsApp messaging is disabled.'
        } else if (entry.phone.phone_type === 'inttext' && !this.twilioHasInternationalMessaging) {
          entry.error = 'This party has an international phone number but international messaging is disabled.'
        }

        res.push(entry)
        return res
      }, [])

      return parties.sort((a, b) => {
        // Parties with errors show last
        if (!a.error && !!b.error) return -1
        if (!b.error && !!a.error) return 1
        // sort by Party name
        const x = (a.name || '').toUpperCase()
        const y = (b.name || '').toUpperCase()
        return ((x < y) ? -1 : ((x > y) ? 1 : 0))
      })
    },

    docketEntryType () {
      return this.docketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    englishLanguageId () {
      return this.optionsLanguages?.find(lang => lang.name === 'English')?.id
    },

    isNote () {
      return this.docketEntry.action_type === 'Note'
    },

    isTask () {
      return this.docketEntry.action_type === 'Task'
    },

    isTwilioConfigured () {
      return this.features?.twilio?.enabled
    },

    isValid () {
      if (!this.isTwilioConfigured) return true
      if (!this.selectedParties.length) return true

      const partiesToNotify = this.notificationsMap.reduce((res, nm) => {
        if (!res.includes(nm.party_id) && !nm._destroy) res.push(nm.party_id)
        return res
      }, [])

      return this.selectedParties.length === partiesToNotify.length
    },

    notificationsMap () {
      const notifications = this.selectedNotifications.reduce((res, sn) => {
        if (!this.selectedParties.includes(sn.partyId)) return res

        const notificationType = this.activeNotificationTypes.find(ant => ant.id === sn.notificationTypeId)
        if (!notificationType) return res

        const editedNotification = this.editedNotifications.
          find(en => en.partyId === sn.partyId && en.notificationTypeId === sn.notificationTypeId && en.text)

        const originalNotification = this.originallySelectedNotifications.
          find(osn => osn.notificationTypeId === sn.notificationTypeId && osn.partyId === sn.partyId)

        res.push({
          docket_entry_type_notification_id: notificationType.id,
          id: originalNotification?.id,
          party_id: sn.partyId,
          template: editedNotification?.text || '',
          trigger_at: notificationType.triggerAt || originalNotification?.triggerAt,
        })

        return res
      }, [])

      this.originallySelectedNotifications.forEach(osn => {
        if (!this.activeNotificationTypeIds.includes(osn.notificationTypeId)) return

        const notification = notifications.find(n => n.id === osn.id)

        if (!notification) {
          notifications.push({
            _destroy: true,
            docket_entry_type_notification_id: osn.notificationTypeId,
            id: osn.id,
            party_id: osn.partyId,
            trigger_at: osn.triggerAt,
          })
        }
      })

      return notifications
    },

    optionsLanguages () {
      return this.getCachedDropdown('languages')
    },

    originallySelectedNotificationTypeIds () {
      return this.originallySelectedNotifications.reduce((res, osn) => {
        if (!res.includes(osn.notificationTypeId)) res.push(osn.notificationTypeId)
        return res
      }, [])
    },

    originallySelectedNotifications () {
      return this.notifications.reduce((res, den) => {
        if (!this.activeParties.includes(den.party_id)) return res

        res.push({
          id: den.id,
          notificationTypeId: den.docket_entry_type_notification_id,
          partyId: den.party_id,
          text: den.template || '',
          triggerAt: den.trigger_at,
        })

        return res
      }, [])
    },

    originallySelectedPartyIds () {
      return this.originallySelectedNotifications.reduce((res, osn) => {
        if (!res.includes(osn.partyId)) res.push(osn.partyId)
        return res
      }, [])
    },

    partyLanguages () {
      const languages = this.availableParties.map(cp => cp.language)
      if (!languages.includes('English')) languages.push('English')
      return languages
    },

    scheduledDate () {
      if (!this.docketEntry?.action_date || !this.docketEntry?.timezone) {
        return null
      }

      if (!(this.isNote || this.isTask) && !this.docketEntry?.event_time_start) {
        return null
      }

      const startTime = this.docketEntry.event_time_start ?? '08:00 AM'
      return moment.
        tz(
          `${this.docketEntry.action_date} ${startTime}`,
          'YYYY-MM-DD hh:mm a',
          this.docketEntry.timezone
        ).
        toDate()
    },

    templateEntities () {
      return {
        DocketEntry: this.docketEntry,
        DocketEntryType: this.docketEntryType,
        LegalCase: this.legalCase,
        OldDocketEntry: this.originalDocketEntry,
        Party: this.notificationEditorDialog.party,
        PrimaryAttorney: this.legalCase.primary_attorney,
        Record: this.record,
      }
    },

    togglePartiesSelectionText () {
      if (this.areAllPartiesSelected) return 'Unselect all parties'
      return 'Select all parties'
    },

    twilioHasInternationalMessaging () {
      return !!this.features?.twilio?.international_enabled
    },

    twilioHasWhatsAppNumber () {
      return !!this.features?.twilio?.has_whatsapp
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'originalDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
      'getCachedDropdown',
    ]),

    ...mapGetters(SHORT_MESSAGE_NAMESPACE, [
      'shortMessageTemplates',
    ]),
  },

  watch: {
    appointmentStartDateTime () {
      this.resetSelectedNotifications()
    },

    docketEntryType: {
      handler () {
        this.resetSelectedNotifications()
      },

      immediate: true,
    },

    isValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },

    legalCase: {
      handler (val) {
        if (!val) return
        if (val.record_id === this.record?.id) return

        this.getRecordById(val.record_id).then(data => { this.record = data })
      },

      immediate: true,
    },

    notificationsMap: {
      deep: true,
      handler (value) { this.$emit('update', value) },
      immediate: true,
    },

    originallySelectedNotifications: {
      deep: true,

      handler () {
        this.resetSelectedParties()
      },

      immediate: true,
    },
  },

  async created () {
    this.isLoading = true

    await this.getFeatures()
    await this.getDropdownOptions({ options: false, path: 'languages' })

    this.isLoading = false
  },

  methods: {
    areAllPartyNotificationsSelected (partyId) {
      return (this.selectedNotificationsForParty(partyId).length || 0) === this.activeNotificationTypes.length
    },

    calculateNotificationTriggerDate (notification) {
      if (!notification.interval || !notification.interval_unit) return null
      if (!this.scheduledDate || !this.docketEntry?.timezone) return null

      const interval = notification.event_type === 'reminder' ? 0 - notification.interval : notification.interval

      return moment.tz(this.scheduledDate, this.docketEntry?.timezone).add(interval, notification.interval_unit)
    },

    isPartyNotificationDisabled (partyId, notificationTypeId) {
      return !(this.selectedNotificationsForParty(partyId).includes(notificationTypeId) &&
        this.activeNotificationTypeIds.includes(notificationTypeId))
    },

    isPartyNotificationEdited (partyId, notificationTypeId) {
      return !!this.editedNotifications.
        find(en => en.partyId === partyId && en.notificationTypeId === notificationTypeId)?.text
    },

    isPartySelected (partyId) {
      return this.selectedParties.includes(partyId)
    },

    onOpenNotificationEditor (party, notificationType) {
      let translation = notificationType.translations.find(nt => nt.language === party.language)
      if (!translation) translation = notificationType.translations.find(nt => nt.language_id === this.englishLanguageId)

      let editedNotification = this.editedNotifications.
        find(en => en.partyId === party.id && en.notificationTypeId === notificationType.id)

      if (!editedNotification) {
        editedNotification = { notificationTypeId: notificationType.id, partyId: party.id, text: '' }
        this.editedNotifications.push(editedNotification)
      }

      this.notificationEditorDialog.notificationTypeId = notificationType.id
      this.notificationEditorDialog.party = party
      this.notificationEditorDialog.template = translation.message
      this.notificationEditorDialog.text = editedNotification.text

      this.notificationEditorDialog.isVisible = true
    },

    openPartyEdit (partyId) {
      this.peSharedQESliderOpen(partyId)
    },

    partyName,

    resetSelectedNotifications () {
      if (!this.originallySelectedNotifications.length) {
        this.selectedNotifications = []
        return
      }

      const notifications = []
      const texts = []

      this.originallySelectedNotifications.forEach(osn => {
        notifications.push({
          notificationTypeId: osn.notificationTypeId,
          partyId: osn.partyId,
        })

        if (osn.text) {
          texts.push({
            notificationTypeId: osn.notificationTypeId,
            partyId: osn.partyId,
            text: osn.text,
          })
        }
      })

      this.selectedNotifications = notifications
      this.editedNotifications = texts
    },

    resetSelectedParties () {
      if (this.originallySelectedPartyIds.length) this.selectedParties = clone(this.originallySelectedPartyIds)
      else this.selectedParties = []

      this.resetSelectedNotifications()
    },

    saveNotificationEditorChanges (text) {
      const notification = this.editedNotifications.
        find(en => {
          return en.notificationTypeId === this.notificationEditorDialog.notificationTypeId &&
            en.partyId === this.notificationEditorDialog.party.id
        })

      notification.text = text

      this.notificationEditorDialog.isVisible = false
    },

    selectedNotificationsForParty (partyId) {
      return this.selectedNotifications.reduce((res, sn) => {
        if (sn.partyId === partyId) res.push(sn.notificationTypeId)
        return res
      }, [])
    },

    togglePartiesSelection () {
      this.selectedParties = this.areAllPartiesSelected ? [] : clone(this.activeParties)
    },

    togglePartyNotificationsSelection (partyId) {
      const selected = this.selectedNotificationsForParty(partyId)
      const unselected = this.activeNotificationTypes.reduce((res, notificationType) => {
        if (!selected.includes(notificationType.id)) {
          res.push({
            notificationTypeId: notificationType.id,
            partyId,
          })
        }

        return res
      }, [])

      if (unselected.length) {
        this.selectedNotifications = [
          ...this.selectedNotifications,
          ...unselected,
        ]
      } else {
        this.selectedNotifications = this.selectedNotifications.filter(sn => sn.partyId !== partyId)
      }
    },

    togglePartyNotificationsSelectionText (partyId) {
      if (this.areAllPartyNotificationsSelected(partyId)) return 'Unselect all'
      return 'Select all'
    },

    togglePartySelection (partyId) {
      const idx = this.selectedParties.findIndex(sp => sp === partyId)

      if (idx >= 0) {
        this.selectedParties.splice(idx, 1)
        return
      }

      this.selectedParties.push(partyId)
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peSharedQESliderOpen',
    ]),

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordById',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
      'getFeatures',
    ]),
  },
}
</script>

<style scoped>
.selectable-party, .selected-party, .error-party, .disabled-party {
  border-radius: 4px;
}

.selectable-party {
  border-color: var(--v-background-darken1);
  margin-bottom: 8px;
}

.selected-party {
  background-color: #545054 !important;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  color: white;
}

.error-party {
  border-color: var(--v-danger-base)
}

.disabled-party {
  color: var(--v-background-darken3);
  border-color: var(--v-background-base);
  margin-bottom: 8px;
}

.notification-list {
  .v-list-item--active::before {
    background-color: transparent;
  }
}

.inset {
  margin-left: 72px;
}

.fill-half-width {
  width: 100%;
  max-width: 50%;
}

.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}

.v-list-item.v-list-item--disabled {
  .v-input--checkbox .v-icon {
    opacity: .5;
  }

  .force-enable {
    pointer-events: all;
  }
}

.v-list-item.v-list-item--disabled {
  .v-input--checkbox .v-icon {
    opacity: .5;
  }

  .force-enable {
    pointer-events: all;
  }
}

.v-list-item.primary--text.v-list-item--disabled {
  opacity: .5 !important;
}
</style>
